import { useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UUID } from '@/types/common';
import { WorkOrderQC } from '@/types/workOrders';
import { useToast } from '@/hooks/useToast';

export const useCreateWorkOrderQC = () => {
  const { toast } = useToast();

  const createWorkOrderQC = useCallback(async (
    workOrderId: UUID, 
    routingId: UUID
  ): Promise<WorkOrderQC[] | null> => {
    try {
      console.log('Creating work order QC checks for Work Order:', workOrderId, 'Routing:', routingId);
      
      // Fetch routing operations that require QC
      const { data: routingOperations, error: fetchError } = await supabase
        .from('routing_operations')
        .select('*')
        .eq('routing_id', routingId)
        .eq('qc_required', true)
        .order('sequence_no', { ascending: true });
      
      if (fetchError) {
        console.error('Error fetching routing operations:', fetchError);
        throw fetchError;
      }
      
      console.log('Found routing operations requiring QC:', routingOperations?.length || 0);
      console.log('Routing operations data:', routingOperations);
      
      if (!routingOperations || routingOperations.length === 0) {
        console.warn('No routing operations requiring QC found for routing:', routingId);
        return [];
      }
      
      // Create work order QC checks from routing operations
      const workOrderQC = routingOperations.map(routingOp => ({
        work_order_id: workOrderId,
        operation_id: routingOp.operation_id,
        qc_point: `${routingOp.code || 'Operation'} Quality Check`,
        result: 'Pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));
      
      console.log('Work order QC checks to insert:', workOrderQC);
      
      // Insert work order QC checks
      const { data, error } = await supabase
        .from('work_order_qc')
        .insert(workOrderQC)
        .select();
      
      if (error) {
        console.error('Error creating work order QC checks:', error);
        console.error('Error details:', error.message, error.details, error.hint);
        throw error;
      }
      
      console.log('Successfully created work order QC checks:', data?.length || 0);
      console.log('Inserted data:', data);
      
      return data;
    } catch (error: any) {
      console.error('Error in createWorkOrderQC:', error);
      toast({
        title: 'Error creating work order QC checks',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  return { createWorkOrderQC };
};
